import { useCallback, useState } from 'react'

import { mutate } from 'swr'

import type { OrderCardActionState, SaveSlotSelection } from '@features/my-gousto-page'
import { cancelOrder, editOrderDelivery } from '@library/api-customer-engagement-experience'

import { GOUSTO_ANYTIME_DASHBOARD_SWR_KEY } from './useGoustoAnytimeDashboard'

const IDLE_STATE: OrderCardActionState = {
  isSaveSlotPending: false,
  hasSaveSlotError: false,
  isSkipDeliveryPending: false,
  hasSkipDeliveryError: false,
}

type MutationFlags = {
  pending: keyof OrderCardActionState
  error: keyof OrderCardActionState
}

type UseGoustoAnytimeOrderActionsResult = {
  saveSlot: (orderId: string, selection: SaveSlotSelection) => Promise<void>
  skipDelivery: (orderId: string) => Promise<void>
  getOrderState: (orderId: string) => OrderCardActionState
}

/**
 * Owns the per-order pending/error state for the editable upcoming-delivery actions
 * (change slot, skip delivery) and revalidates the dashboard once a mutation succeeds
 * so the order section re-renders with fresh data.
 */
export function useGoustoAnytimeOrderActions(): UseGoustoAnytimeOrderActionsResult {
  const [states, setStates] = useState<Record<string, OrderCardActionState>>({})

  const patch = useCallback((orderId: string, changes: Partial<OrderCardActionState>) => {
    setStates((prev) => ({
      ...prev,
      [orderId]: { ...IDLE_STATE, ...prev[orderId], ...changes },
    }))
  }, [])

  // Both delivery mutations share the same flow — flag pending, run the request, then
  // on success revalidate the dashboard — and differ only in which flag pair they toggle.
  const runMutation = useCallback(
    async (orderId: string, flags: MutationFlags, request: () => Promise<void>) => {
      patch(orderId, { [flags.pending]: true, [flags.error]: false })

      try {
        await request()
      } catch {
        patch(orderId, { [flags.pending]: false, [flags.error]: true })

        return
      }

      await mutate(GOUSTO_ANYTIME_DASHBOARD_SWR_KEY)
      patch(orderId, { [flags.pending]: false })
    },
    [patch],
  )

  const saveSlot = useCallback(
    (
      orderId: string,
      {
        delivery_day_id: deliveryDayId,
        delivery_slot_id: deliverySlotId,
        day_slot_lead_time_id: daySlotLeadTimeId,
      }: SaveSlotSelection,
    ) =>
      runMutation(orderId, { pending: 'isSaveSlotPending', error: 'hasSaveSlotError' }, () =>
        editOrderDelivery(orderId, { deliveryDayId, deliverySlotId, daySlotLeadTimeId }),
      ),
    [runMutation],
  )

  const skipDelivery = useCallback(
    (orderId: string) =>
      runMutation(
        orderId,
        { pending: 'isSkipDeliveryPending', error: 'hasSkipDeliveryError' },
        () => cancelOrder(orderId),
      ),
    [runMutation],
  )

  const getOrderState = useCallback(
    (orderId: string): OrderCardActionState => states[orderId] ?? IDLE_STATE,
    [states],
  )

  return { saveSlot, skipDelivery, getOrderState }
}
